import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import type {
  EmployeeIdentity as SharedEmployeeIdentity,
  EmployeeShiftAccessUpdate,
  EmployeeShiftAccessView,
  EmployeeRole,
  EmployeeDepartment,
  EmployeeView as SharedEmployeeView,
  EmployeesImportResult,
  IdResponse,
} from 'shared-models/browser';

export type EmployeeView = SharedEmployeeView & {
  photoUrl?: string | null;
};

export type EmployeeIdentity = SharedEmployeeIdentity;

export type EmployeeIdentifyResponse =
  | { employee: EmployeeIdentity; employeeSession: string; activeSessionId: string; conflict?: never }
  | { employee: null; conflict: { existingSessionId: string }; employeeSession?: never; activeSessionId?: never };

export type CreateEmployeeRequest = {
  fullName: string;
  pin: string;
  role: EmployeeRole;
  baseHourlySalary?: number | null;
  departments?: EmployeeDepartment[];
};

export type UpdateEmployeeRequest = {
  fullName?: string;
  pin?: string;
  role?: EmployeeRole;
  baseHourlySalary?: number | null;
  departments?: EmployeeDepartment[];
  shiftAccess?: EmployeeShiftAccessUpdate;
};

export type EmployeeShiftAccessForm = {
  hasShiftAccess: boolean;
  enabled: boolean;
  username: string;
  password: string;
  confirmPassword: string;
  role: 'manager' | 'worker';
  canEditEmployees: boolean;
  canAssignEmployees: boolean;
  canManageSchedule: boolean;
  canCreateEmployees: boolean;
};

class EmployeesService {
  // Summary: Loads active tenant employees for POS entry selection so floor login can choose valid active identities only.
  // Details: docs/function-library/web/services.md#employees-service
  async listActive(tenantId: string): Promise<EmployeeView[]> {
    try {
      const response = await authenticatedInstance.get<EmployeeView[]>(`/employees/tenant/${tenantId}/active`);
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Checks whether an employee already has an active session on another device, before PIN entry.
  async checkSession(tenantId: string, employeeId: number): Promise<{ occupied: boolean; existingSessionId?: string }> {
    try {
      const response = await authenticatedInstance.get<{ occupied: boolean; existingSessionId?: string }>(
        `/employees/tenant/${tenantId}/session-status/${employeeId}`,
      );
      return response.data;
    } catch {
      return { occupied: false };
    }
  }

  // Summary: Identifies employee by selected employee id and PIN and returns employee session token for POS-scoped manager authorization.
  // Details: docs/function-library/web/services.md#employees-service
  async identify(tenantId: string, payload: {
    employeeId: number;
    pin: string;
  }): Promise<EmployeeIdentifyResponse> {
    try {
      const response = await authenticatedInstance.post<EmployeeIdentifyResponse>(
        `/employees/tenant/${tenantId}/identify`,
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Confirms a takeover after user acknowledged the conflict warning — re-validates PIN, kicks the other device via SSE, and returns fresh employee session.
  async takeover(tenantId: string, payload: {
    employeeId: number;
    pin: string;
    existingSessionId: string;
  }): Promise<{ employee: EmployeeIdentity; employeeSession: string; activeSessionId: string }> {
    try {
      const response = await authenticatedInstance.post<{ employee: EmployeeIdentity; employeeSession: string; activeSessionId: string }>(
        `/employees/tenant/${tenantId}/takeover`,
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Clears the active session slot for the logged-in employee on voluntary logout so the next login from any device doesn't trigger the conflict warning.
  async clearActiveSession(tenantId: string, employeeSession: string, sessionId: string): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/employees/tenant/${tenantId}/clear-active-session`,
        { sessionId },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch {
      // best-effort — don't block logout if this fails
    }
  }

  async acknowledgePresenceProbe(tenantId: string, employeeSession: string, probeId: string): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/employees/tenant/${tenantId}/presence-probe/ack`,
        { probeId },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch {
      // Best-effort ack: probe timeout on the requester side handles failures.
    }
  }

  // Summary: Loads full employee settings list (active and inactive) for manager POS settings panel using employee session header.
  // Details: docs/function-library/web/services.md#employees-service
  async listForSettings(tenantId: string, employeeSession: string): Promise<EmployeeView[]> {
    try {
      const response = await authenticatedInstance.get<EmployeeView[]>(`/employees/tenant/${tenantId}`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async downloadImportTemplate(tenantId: string, employeeSession: string): Promise<Blob> {
    try {
      const response = await authenticatedInstance.get(`/employees/tenant/${tenantId}/import-template`, {
        headers: { 'x-employee-session': employeeSession },
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async importXlsx(tenantId: string, file: File, employeeSession: string): Promise<EmployeesImportResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authenticatedInstance.post<EmployeesImportResult>(
        `/employees/tenant/${tenantId}/import`,
        formData,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Creates employee from manager settings while passing employee session token required by backend manager authorization guard.
  // Details: docs/function-library/web/services.md#employees-service
  async create(
    tenantId: string,
    payload: CreateEmployeeRequest,
    employeeSession: string,
  ): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>(`/employees/tenant/${tenantId}`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Updates employee profile details from manager settings with employee session authorization context.
  // Details: docs/function-library/web/services.md#employees-service
  async update(
    employeeId: number,
    payload: UpdateEmployeeRequest,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/employees/${employeeId}`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async getShiftAccess(employeeId: number, employeeSession: string): Promise<EmployeeShiftAccessView> {
    try {
      const response = await authenticatedInstance.get<EmployeeShiftAccessView>(
        `/employees/${employeeId}/shift-access`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Uploads a manager-provided employee photo to backend image pipeline, which normalizes and stores the image.
  // Details: docs/function-library/web/services.md#employees-service
  async uploadPhoto(
    employeeId: number,
    photoFile: File,
    employeeSession: string,
  ): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);

      await authenticatedInstance.post<void>(
        `/employees/${employeeId}/photo`,
        formData,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Deactivates employee from manager settings with employee session authorization context.
  // Details: docs/function-library/web/services.md#employees-service
  async deactivate(employeeId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/employees/${employeeId}/deactivate`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      const message = extractServiceErrorMessage(error, '');
      throw new Error(message === 'Cannot remove the last active SHIFT manager'
        ? 'לא ניתן להשבית את העובד מכיוון שהוא מנהל ה-SHIFT הפעיל האחרון בחברה.'
        : message);
    }
  }

  // Summary: Reactivates employee from manager settings with employee session authorization context.
  // Details: docs/function-library/web/services.md#employees-service
  async activate(employeeId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/employees/${employeeId}/activate`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const employeesService = new EmployeesService();





